import cx from "classnames";
import { graphql, withAssetPrefix } from "gatsby";
import BackgroundImage from "gatsby-background-image";
import React from "react";
import { getSrc, getImage } from "gatsby-plugin-image";
import { convertToBgImage } from "gbimage-bridge";
import Certificates from "../../components/Certificates";
import Layout from "../../components/layout";
import Facts from "../../components/Facts";
import Image from "../../components/GatsbyImage";
import InternalLink from "../../components/InternalLink";
import ReadMoreWrapper from "../../components/ReadMoreWrapper";
import HomeVideo from "./HomeVideo";
import HomeWidget from "../sustainability/components/HomeWidget";
import "../../graphql-fragments";
import { BenefitImageList } from "../../components/BenefitImageList";
import { useCallOnce } from "../../hooks/useCallOnce";
import { trackCustomEvent } from "../../utilities/analytics";

const Home = ({ data }) => {
  const { allSustainabilityCategory, home } = data;

  const {
    absolutePath,
    brand,
    certificate,
    messagesNodes,
    coreMessage,
    secondaryMessage,
    productsNodes,
    lexicon,
    pageType,
    traits,
    sustainabilityWidgetWebsite,
    homeVideo,
    homepageVideoHero,
  } = home;

  const bgImage = convertToBgImage(
    getImage(coreMessage?.summaryBackground?.file?.fileLocal)
  );

  const secondaryMessageBgImage = convertToBgImage(
    getImage(secondaryMessage?.summaryBackground?.file?.fileLocal)
  );

  const trackVideoPlay = useCallOnce(() => {
    trackCustomEvent({
      action: "Video Play",
      category: "Engagement",
      label: "Hero Video",
    });
  });

  const trackVideoEnded = useCallOnce(() => {
    trackCustomEvent({
      action: "Video Ended",
      category: "Engagement",
      label: "Hero Video",
    });
  });

  return (
    <Layout
      {...{
        absolutePath,
        home,
        metaDescription: brand.description,
        pageTitle: home.homepageTitle,
        pageType,
        shareImgSrc: getSrc(coreMessage?.hero.file.fileLocal),
        traits,
      }}
    >
      <div className="flex flex-col">
        <h1
          data-cy="primary-heading"
          className="order-2 text-center title title--primary"
        >
          {coreMessage?.title}
        </h1>
        {coreMessage?.summary && (
          <div
            data-cy="summary-text"
            className={cx(`summary core-summary order-3`, {
              "summary--without-bg": !coreMessage?.summaryBackground,
              "summary--primary": coreMessage?.summary.length < 90,
              "summary--secondary text-center":
                coreMessage?.summary.length >= 90,
            })}
          >
            {coreMessage?.summaryBackground ? (
              <BackgroundImage
                Tag="span"
                {...bgImage}
                alt={coreMessage?.summaryBackground.description}
                className="summary-background-text"
              >
                {coreMessage?.summary}
              </BackgroundImage>
            ) : (
              <span className="mx-auto summary-background-text">
                {coreMessage?.summary}
              </span>
            )}
          </div>
        )}
        {homepageVideoHero ? (
          <video
            className="order-1 w-full"
            controls
            data-testid="hero-video"
            data-cy="hero-video"
            poster={withAssetPrefix(
              homepageVideoHero.file.fileLocal.videoH264.screenshots[0].path
            )}
            preload="none"
            onPlay={trackVideoPlay}
            onEnded={trackVideoEnded}
          >
            <source
              src={withAssetPrefix(
                homepageVideoHero.file.fileLocal.videoH264.path
              )}
              type="video/mp4"
            />
          </video>
        ) : (
          <div className="order-1" data-cy="hero-image">
            <Image
              image={getImage(coreMessage?.hero.file.fileLocal)}
              alt={coreMessage?.hero.description}
              loading="eager"
            />
          </div>
        )}
      </div>
      {coreMessage?.benefitImages ? (
        <BenefitImageList images={coreMessage?.benefitImages} />
      ) : (
        coreMessage?.benefits && (
          <section className="benefits arrow-b arrow-b--benefits">
            <ul className="max-w-md mx-auto">
              {coreMessage?.benefits.map((text) => (
                <li key={text} className="pl-4 mb-3 leading-tight list-disc">
                  {text}
                </li>
              ))}
            </ul>
          </section>
        )
      )}
      {coreMessage?.benefitsDisclaimer && (
        <p className="max-w-lg m-10 md:mx-auto benefits-disclaimer">
          <small>{coreMessage?.benefitsDisclaimer}</small>
        </p>
      )}
      {coreMessage?.main && (
        <ReadMoreWrapper
          href={coreMessage?.path}
          lexicon={lexicon}
          htmlTruncated={coreMessage?.main.htmlTruncated}
          messageTitle={coreMessage?.title}
        />
      )}
      <Facts facts={coreMessage?.facts} lexicon={lexicon} />
      {sustainabilityWidgetWebsite &&
        allSustainabilityCategory?.nodes.length === 3 && (
          <HomeWidget
            categories={allSustainabilityCategory.nodes}
            widget={sustainabilityWidgetWebsite}
          />
        )}
      <section data-cy="products" className="pb-20 products bg-grey">
        <h2 className="text-center title">{lexicon.products}</h2>
        {messagesNodes?.length > 0 ? (
          <ul className="container flex flex-wrap justify-center px-5 promo">
            {messagesNodes.map(({ hero, path, teaserImage, title }) => {
              const {
                description,
                file: { fileLocal },
              } = teaserImage || hero;

              return (
                <li
                  key={path}
                  className="m-2 bg-white rounded shadow flex-basis-full lg:flex-basis-1/3 lg:max-w-xs"
                >
                  <InternalLink href={path} className="block">
                    <Image image={getImage(fileLocal)} alt={description} />
                    <span className="block promo-title">{title}</span>
                  </InternalLink>
                </li>
              );
            })}
          </ul>
        ) : (
          productsNodes?.length > 0 && (
            <div className="container">
              <div className={"flex flex-wrap justify-center pb-5"}>
                {productsNodes.map(({ path, name, variants }, i) => {
                  const [
                    {
                      packshot: {
                        description: alt,
                        file: { fileLocal },
                      },
                    },
                  ] = variants;
                  return (
                    <InternalLink
                      key={i}
                      className="m-2 bg-white rounded shadow flex-basis-2/5 lg:max-w-xs"
                      href={path}
                    >
                      <Image
                        className="mt-5"
                        image={getImage(fileLocal)}
                        alt={alt}
                      />
                      <span className="block promo-title">{name}</span>
                    </InternalLink>
                  );
                })}
              </div>
            </div>
          )
        )}
      </section>
      {secondaryMessage && (
        <>
          <h2 className="text-center title">{secondaryMessage.title}</h2>

          <div className="summary-hero-wrapper">
            {secondaryMessage.summary && (
              <div
                className={cx("summary text-center summary--secondary", {
                  "summary--without-bg": !secondaryMessage.summaryBackground,
                })}
              >
                {secondaryMessage.summaryBackground ? (
                  <BackgroundImage
                    Tag="span"
                    {...secondaryMessageBgImage}
                    alt={secondaryMessage.summaryBackground.description}
                    className="summary-background-text"
                  >
                    {secondaryMessage.summary}
                  </BackgroundImage>
                ) : (
                  <span className="mx-auto summary-background-text">
                    {secondaryMessage.summary}
                  </span>
                )}
              </div>
            )}

            <Image
              image={getImage(secondaryMessage.hero.file.fileLocal)}
              alt={secondaryMessage.hero.description}
            />
          </div>
          {secondaryMessage.main && (
            <ReadMoreWrapper
              href={secondaryMessage.path}
              lexicon={lexicon}
              htmlTruncated={secondaryMessage.main.htmlTruncated}
              messageTitle={secondaryMessage.title}
            />
          )}

          <Facts facts={secondaryMessage.facts} lexicon={lexicon} />
        </>
      )}
      {homeVideo && <HomeVideo homeVideo={homeVideo} />}
      <Certificates {...{ lexicon, certificate }} />
    </Layout>
  );
};

export const query = graphql`
  query HomeQuery {
    allSustainabilityCategory {
      nodes {
        ...HomeWidgetCategory
      }
    }
    home {
      ...homeFields
      traits {
        traitsStringified
      }
      path
      lexicon {
        home
      }
      messagesNodes {
        path
        title
        hero {
          ...MessageCardMedia
        }
        teaserImage {
          ...MessageCardMedia
        }
      }
      sustainabilityWidgetWebsite {
        ...HomeWidget
      }
      homeVideo {
        ...HomeVideoData
      }
      homepageVideoHero {
        file {
          fileLocal {
            videoH264(screenshots: "1", screenshotWidth: 1280, maxWidth: 1280) {
              path
              screenshots {
                path
              }
            }
          }
        }
      }
    }
  }
`;

export default Home;
